import React from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { CoverPlaceholder } from '../../strip-board/components/cover-placeholder';
import { AddStripMenu } from './add-strip-menu';
import { Stripboard } from '../../../app/entities/stripboard';
import { CollapsibleItem } from '../../../components/collapsible-item/collapsible-item';
import { timeParser } from '../../../lib/utils/time-parser';
import { getRenderedTime } from '../../../lib/utils/Convertor';
import {
  CellContent,
  CellLabel,
  HorizontalCell,
  VerticalCell,
} from '../../shootingdays/components/styled-mobile-table';
import { CoverItem, Image, HiddenCoverOverlay } from './styled-strips';
import { Shot } from '../../../app/entities/shot';

import type { ShotStrip } from '../../../app/entities/strip';
import type { Frame } from '../../../lib/utils/transform-image';
import type { Property } from '../../../app/entities/property';
import { PropertyType } from '@producer/properties';
import { TagsViewer } from '../../tags/components/tags-viewer';
import { minutesFormatter } from '../../../lib/time-formatters';

type MobileShotStripProps = {
  strip: ShotStrip;
  stripboard: Stripboard;
  properties: Property[];
};

export const MobileShotStripComponent: React.FC<MobileShotStripProps> = observer(
  ({ stripboard, strip, properties }) => {
    const shot = Shot.getOne(strip.data.shotId);
    const locations = shot?.project?.locations;
    const coverProp = properties.find((property) => property.key === 'cover');

    const renderFixedProperties = (key: Property['key']) => {
      if (!shot) return;

      const targetProp = properties.find((property) => property.key === key);
      if (!targetProp) return;

      const value = shot.getProperty(targetProp._id);

      switch (key) {
        case 'cover':
          return (
            <CoverItem>
              {shot?.cover?.src ? (
                <Image src={shot?.cover?.src || ''} />
              ) : (
                <CoverPlaceholder onUpload={(file) => handleEdit({ file })} />
              )}

              <HiddenCoverOverlay data-hide={!strip.data.isHidden} />
            </CoverItem>
          );
        case 'title':
          return value && <ShotTitle>{value}</ShotTitle>;
        case 'order':
          return (
            <HeaderData>
              <CellLabel>{targetProp.name}</CellLabel>

              <CellContent>{value}</CellContent>
            </HeaderData>
          );
        case 'startTime':
          return (
            <HeaderData>
              <CellLabel>{targetProp.name}</CellLabel>

              <CellContent>{getRenderedTime(strip.startTime)}</CellContent>
            </HeaderData>
          );
        case 'estimatedTime':
          return (
            !strip.data.isHidden && (
              <HeaderData>
                <CellLabel>{targetProp.name}</CellLabel>

                <CellContent>{minutesFormatter(value || 0)}</CellContent>
              </HeaderData>
            )
          );

        default:
          return null;
      }
    };

    const renderProperty = (property: Property) => {
      if (!shot) return;

      if (['cover', 'title', 'order', 'startTime', 'estimatedTime'].includes(property.key)) {
        return;
      }

      const value = shot.getProperty(property._id);

      const multiValue = Array.isArray(value) ? value.join(', ') : value;

      if (['timing', 'setting'].includes(property.key)) {
        return (
          <HorizontalCell>
            <CellLabel>{property.name}</CellLabel>

            <CellContent style={{ textTransform: 'capitalize' }}>{value}</CellContent>
          </HorizontalCell>
        );
      }

      switch (property.type) {
        case PropertyType.SingleSelect:
          if (property.resourceType === 'Location') {
            const location = locations?.find((location) => location._id === shot?.locationId);

            return (
              <HorizontalCell>
                <CellLabel>{property.name}</CellLabel>

                <CellContent>{location?.name}</CellContent>
              </HorizontalCell>
            );
          }

          return (
            <VerticalCell key={property._id}>
              <CellLabel>{property.name}</CellLabel>

              <CellContent>{value}</CellContent>
            </VerticalCell>
          );
        case PropertyType.MultiSelect:
          if (property.resourceType === 'Character') {
            const characters = shot?.characters?.map((character) => character.name).join(', ');

            return (
              <VerticalCell key={property._id}>
                <CellLabel>{property.name}</CellLabel>

                {characters && <CellContent>{characters}</CellContent>}
              </VerticalCell>
            );
          } else if (property.resourceType === 'Tag') {
            return (
              <VerticalCell>
                <CellLabel>{property.name}</CellLabel>

                <TagsViewer
                  key={property._id}
                  targetId={shot._id}
                  targetType="shot"
                  overflow="wrap"
                  alignment="start"
                />
              </VerticalCell>
            );
          }

          return (
            <VerticalCell key={property._id}>
              <CellLabel>{property.name}</CellLabel>

              <CellContent>{multiValue}</CellContent>
            </VerticalCell>
          );
        case PropertyType.LongText:
          return (
            <VerticalCell key={property._id}>
              <CellLabel>{property.name}</CellLabel>

              <CellContent
                dangerouslySetInnerHTML={{
                  __html: value,
                }}
              />
            </VerticalCell>
          );
        case PropertyType.SmallText:
        case PropertyType.Number:
          return (
            <VerticalCell key={property._id}>
              <CellLabel>{property.name}</CellLabel>

              <CellContent>{value}</CellContent>
            </VerticalCell>
          );
        case PropertyType.Duration:
          return (
            <VerticalCell key={property._id}>
              <CellLabel>{property.name}</CellLabel>

              <CellContent>{timeParser(value || 0).toString()}</CellContent>
            </VerticalCell>
          );

        //TODO: add the rest of the property types
        default:
          return;
      }
    };

    const handleEdit = async (data: Partial<Shot> & { file?: Frame }) => {
      if (!shot) {
        return;
      }

      shot.update(data);
    };

    if (!shot) {
      return;
    }

    return (
      <Container>
        <CollapsibleItem
          title={
            <Header>
              {renderFixedProperties('cover')}

              <HeaderDataWrapper data-hidden={strip.data.isHidden}>
                {!coverProp && (
                  <ShotTitle data-placeholder="true">Expand to see more shot properties</ShotTitle>
                )}

                {renderFixedProperties('title')}
                {renderFixedProperties('order')}
                {renderFixedProperties('startTime')}
                {renderFixedProperties('estimatedTime')}
              </HeaderDataWrapper>
            </Header>
          }
          isTransparent
        >
          <ContentContainer data-hidden={!!strip.data.isHidden}>
            {properties.map((property) => (
              <React.Fragment key={property._id}>{renderProperty(property)}</React.Fragment>
            ))}

            <HorizontalCell>
              <CellLabel>Actions</CellLabel>

              <AddStripMenu
                stripId={strip._id}
                projectId={stripboard.projectId}
                position={strip.position}
                shot={shot}
                stripType="shot"
              />
            </HorizontalCell>
          </ContentContainer>
        </CollapsibleItem>
      </Container>
    );
  },
);

const Container = styled.div`
  background-color: var(--color-grayscale-charleston);
  border-radius: 0.4rem;

  & > * {
    #collapsible-trigger {
      padding: 0.8rem;

      & > div:first-child {
        width: 100%;
      }

      & button {
        background-color: var(--color-grayscale-tuna);
      }
    }
  }
`;

const ContentContainer = styled.div`
  padding: 0 1.6rem 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;

  &[data-hidden='true'] {
    ${CellContent} {
      color: var(--color-grayscale-trout);
    }
  }
`;

const ShotTitle = styled.h3`
  color: var(--color-grayscale-white);
  font-size: 1.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &[data-placeholder='true'] {
    display: block;
    color: var(--color-grayscale-light-slate);

    &:not(:only-child) {
      display: none;
    }
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.4rem;
  min-height: 3.2rem;

  & > :only-child {
    justify-content: center;
  }

  & ${CoverItem} {
    min-width: 12rem;
  }
`;

const HeaderDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 0.4rem;
  width: 100%;
  padding-inline-start: 0.8rem;

  &[data-hidden='true'] {
    ${ShotTitle} , ${CellContent} {
      color: var(--color-grayscale-trout);
    }
  }
`;

export const HeaderData = styled(HorizontalCell)`
  width: 100%;
`;
